import { useEffect, useState } from "react";
import axios from "axios";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { Layers } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const compact = (n) => new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(n);
const day = (iso) => new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short" });
const hm = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

const Chip = ({ children, testid, tone = "text-muted-foreground" }) => (
  <span className={`rounded-md border border-white/10 bg-white/[0.03] px-2 py-1 font-mono text-xs ${tone}`} data-testid={testid}>{children}</span>
);

const CycleCard = ({ c }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="stacks-cycle-card">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-sm text-muted-foreground">Current cycle</p>
        <p className="mt-2 inline-block rounded-xl bg-white/[0.05] px-4 py-1 font-heading text-4xl font-black" data-testid="stacks-cycle-id">{c.id}</p>
      </div>
      <span className="flex items-center gap-2 rounded-full border border-white/10 px-3 py-1.5 text-xs" data-testid="stacks-cycle-ends">
        <span className="h-2 w-2 rounded-full bg-green-400 animate-pulse" />Ends in ~{Math.ceil(c.days_left)} days
      </span>
    </div>
    <p className="mt-4 text-2xl font-semibold tabular-nums" data-testid="stacks-cycle-stacked">
      Ӿ{compact(c.stacked_stx)} STX
      {c.stacked_usd && <span className="text-muted-foreground"> / ${compact(c.stacked_usd)} stacked</span>}
    </p>
    <div className="mt-5 flex justify-between text-xs text-muted-foreground"><span>Started</span><span>Ends</span></div>
    <div className="mt-2 h-1.5 rounded-full bg-white/10">
      <div className="h-full rounded-full bg-primary shadow-[0_0_12px] shadow-primary/60 transition-[width] duration-700" style={{ width: `${c.progress * 100}%` }} data-testid="stacks-cycle-progress" />
    </div>
    <div className="mt-3 flex justify-between"><Chip>~{day(c.started_at)}</Chip><Chip>~{day(c.ends_at)}</Chip></div>
    <div className="mt-3 flex flex-wrap justify-between gap-2">
      <div className="flex gap-2">
        <Chip testid="stacks-btc-start" tone="text-orange-400">₿ #{c.btc_start_height}</Chip>
        <Chip testid="stacks-btc-now" tone="text-primary">₿ now #{c.btc_current_height}</Chip>
      </div>
      <Chip testid="stacks-btc-end">₿ #{c.btc_end_height}</Chip>
    </div>
  </div>
);

const NetworkCard = ({ n, metric, setMetric }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="stacks-network-card">
    <div className="grid grid-cols-2 gap-2">
      {[["tx", "Transactions", n.tx_6h], ["blocks", "Blocks mined", n.blocks_6h]].map(([k, label, v]) => (
        <button key={k} type="button" onClick={() => setMetric(k)} data-testid={`stacks-metric-${k}`}
          className={`rounded-xl p-3 text-left transition-colors ${metric === k ? "bg-white/[0.06]" : "opacity-60 hover:opacity-100"}`}>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums">{v.toLocaleString()} <span className="text-xs font-normal text-muted-foreground">Last 6hs</span></p>
        </button>
      ))}
    </div>
    <div className="mt-4 h-40" data-testid="stacks-network-chart">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={n.series} margin={{ top: 5, right: 0, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="stxArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#fb923c" stopOpacity={0.45} />
              <stop offset="100%" stopColor="#fb923c" stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis dataKey="t" tickFormatter={hm} tick={{ fill: "#71717a", fontSize: 10 }} axisLine={{ stroke: "#27272a" }} tickLine={false} minTickGap={40} />
          <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #27272a", borderRadius: 8, fontSize: 12 }} labelFormatter={hm} />
          <Area type="linear" dataKey={metric} name={metric === "tx" ? "Transactions" : "Blocks"} stroke="#fb923c" strokeWidth={2} fill="url(#stxArea)" isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  </div>
);

export const StacksNetworkStats = () => {
  const [data, setData] = useState(null);
  const [metric, setMetric] = useState("tx");

  useEffect(() => {
    const load = () => axios.get(`${API}/stacks/network`).then((r) => setData(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  if (!data) return null;
  return (
    <section className="px-6 pb-12" data-testid="stacks-network-stats">
      <div className="max-w-7xl mx-auto">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h2 className="flex items-center gap-2 font-heading text-base md:text-lg tracking-wider">
            <Layers className="h-5 w-5 text-primary" />STACKS NETWORK
          </h2>
          <a href="https://explorer.hiro.so" target="_blank" rel="noopener noreferrer" data-testid="stacks-network-source"
            className="flex items-center gap-2 font-mono text-[11px] uppercase text-muted-foreground hover:text-primary">
            <span className="h-1.5 w-1.5 rounded-full bg-green-400 animate-pulse" />Live · Hiro · Stacks tip #{data.network.stacks_tip_height.toLocaleString()}
          </a>
        </div>
        <div className="grid gap-5 lg:grid-cols-2">
          <CycleCard c={data.cycle} />
          <NetworkCard n={data.network} metric={metric} setMetric={setMetric} />
        </div>
      </div>
    </section>
  );
};
